import { RootDispatch } from "../types";
import { ParticipantUser, User } from "./types";
import { get } from "../../utils/fetch";
import history from "../../history";
import logging from "../../utils/logging";
import { unsubscribeAllChannels, configurePubnub } from "../pubnub/actions";
interface LogoutAction {
  type: "session/logout";
}

interface DestroySessionAction {
  type: "session/destroy-session";
}

interface FetchCurrentUserAction {
  type: "session/fetch-current-user";
}

interface FetchCurrentUserErrorAction {
  type: "session/fetch-current-user-error";
  errorMessage: string;
}

export const SESSION_FETCHED_CURRENT_USER = "session/fetched-current-user";
interface FetchedCurrentUserAction {
  type: typeof SESSION_FETCHED_CURRENT_USER;
  user: User;
}

export const SESSION_USE_EMBEDDED_THEME = "session/use-embedded-theme";
interface UseEmbedMobileAppThemeAction {
  type: typeof SESSION_USE_EMBEDDED_THEME;
}

export type Action =
  | LogoutAction
  | DestroySessionAction
  | FetchCurrentUserAction
  | FetchedCurrentUserAction
  | FetchCurrentUserErrorAction
  | { type: "session/fetch-session-last-activity-started" }
  | { type: "session/fetch-session-last-activity-finished" }
  | UseEmbedMobileAppThemeAction
  | { type: "session/fetch-participant-user" }
  | { type: "session/fetch-participant-user-error"; errorMessage: string }
  | { type: "session/fetch-participant-user-success"; user: User };

export function fetchCurrentUser() {
  return async (dispatch: RootDispatch) => {
    dispatch({ type: "session/fetch-current-user" });

    const resp = await get<User>("/api/users/current/");

    if (resp.status === 403) {
      dispatch(unsubscribeAllChannels(true));
      dispatch({ type: "session/destroy-session" });
      history.push("/");
    } else if (resp.error != null) {
      dispatch({ type: "session/fetch-current-user-error", errorMessage: resp.error.message });
    } else if (resp.data != null) {
      dispatch({ type: SESSION_FETCHED_CURRENT_USER, user: resp.data });
      if (history.location.pathname === "/") {
        history.push("/dashboard/");
      }
      dispatch(configurePubnub(resp.data.pubnubKeys, resp.data.id));
    }
  };
}

export function fetchParticipantUser() {
  return async (dispatch: RootDispatch) => {
    dispatch({ type: "session/fetch-participant-user" });

    const resp = await get<ParticipantUser>("/api/participants/current/");

    if (resp.error != null) {
      dispatch({
        type: "session/fetch-participant-user-error",
        errorMessage: resp.error.message
      });
    } else if (resp.data != null) {
      dispatch(setParticipantUser(resp.data));
    }
  };
}

export const setParticipantUser = (participantUser: any) => async (dispatch: RootDispatch) => {
  const newUser: User = {
    id: participantUser.systemUserId,
    organizations: [],
    username: participantUser.fullName,
    email: "",
    firstName: participantUser.firstName,
    lastName: participantUser.lastName,
    pubnubKeys: participantUser.pubnubKeys
  };
  dispatch({ type: "session/fetch-participant-user-success", user: newUser });
  dispatch(configurePubnub(newUser.pubnubKeys, newUser.id));
};

interface LastActivity {
  inactiveSeconds: number;
  maxAllowedInactiveSeconds: number;
}

declare global {
  // eslint-disable-next-line no-var
  var checkSessionStatusTimeoutHandle: NodeJS.Timeout;
}

export const checkSessionStatus = (attempt = 1) => async (dispatch: RootDispatch) => {
  if (attempt > 13) {
    dispatch(unsubscribeAllChannels(true));
    dispatch({ type: "session/destroy-session" });
    history.push("/");
    return;
  }

  dispatch({ type: "session/fetch-session-last-activity-started" });

  let resp = null;
  try {
    //api calls can throw unhandled exceptions, example: net::ERR_CONNECTION_REFUSED
    //status code 403 forbidden is handled in the util/responses.ts/getError
    resp = await get<LastActivity>("/session_last_activity/");
  } catch (ex) {
    if (resp && resp.status === 403) {
      logging.info(ex);
    } else {
      logging.Sentry.captureException(ex);
    }
  }
  dispatch({ type: "session/fetch-session-last-activity-finished" });

  if (resp && resp.data && resp.data.maxAllowedInactiveSeconds) {
    if (resp.data.inactiveSeconds > resp.data.maxAllowedInactiveSeconds) {
      dispatch(unsubscribeAllChannels(true));
      dispatch({ type: "session/destroy-session" });
      history.push("/");
      return;
    }
    //calculate the earliest time that the session could have expired by
    // eslint-disable-next-line prettier/prettier
    const nextRunAt =
      (resp.data.maxAllowedInactiveSeconds - resp.data.inactiveSeconds) * 1000 + 500;
    window.checkSessionStatusTimeoutHandle = setTimeout(() => {
      dispatch(checkSessionStatus()); //Session might have expired time to go check again.
    }, nextRunAt);
  } else {
    if (history.location.pathname !== "/") {
      const tryAgainIn = Math.pow(2, attempt);
      window.checkSessionStatusTimeoutHandle = setTimeout(() => {
        dispatch(checkSessionStatus(attempt + 1));
      }, tryAgainIn * 1000);
    }
  }
};

export const useEmbedTheme = () => async (dispatch: RootDispatch) => {
  dispatch({ type: SESSION_USE_EMBEDDED_THEME });
};
